import { User } from '@features/users/types';
import { SchemeID } from '@pages/schemes/types';
import { AccessEnum } from '@shared/types';

export type DateRange = {
    startDate: Date;
    endDate: Date;
};

export type DateRangeWithOffset = DateRange & {
    offset: number;
};

export type WeekRanges = {
    list: DateRangeWithOffset[];
    rangesByOffset: Record<number, DateRangeWithOffset>; // access O(1) by offset
};

export type CredentialsType = { entry_point?: string | null; password?: string | null };

export type Booking = {
    pk: number;
    device: Device['pk'];
    status: BookingStatus;
    comment: string;
    booked_from: Date | string;
    booked_to: Date | string;
    access: AccessEnum;
    created_by_user: User['pk'];
    entry_point?: CredentialsType['entry_point'];
    password?: CredentialsType['password'];
    slots?: SlotIndex[];
    isLocal?: boolean;
    readonly?: boolean;
};

export type DeviceArchitecture = 'x86' | 'arm';

export type Device = {
    pk: number;
    name: string;
    office: DevicesLocation['name'];
    architecture: DeviceArchitecture;
    workload: number;
    type: DevicesType;
    slots_count: number;
    ports_count: number;
};

export type DeviceForCredentials = Pick<Device, 'architecture' | 'type'>;

export type DeviceFullInfo = Omit<Device, 'workload'> & {
    comment: string;
    location: string;
    position: string;
    rack: string;
    image: string;
    ports: string;
    netbox: string;
    traffic_generators: Device[] | null;
    credentials?: Pick<Booking, 'entry_point' | 'password'>;
};

export type DevicesSearch = string;

export type DevicesLocation = {
    pk: number;
    location: string;
    name: string;
    unit: string;
};

export type DevicesLocationSelect = DevicesLocation['name'];

export enum DevicesType {
    DUT = 'dut',
    IXIA = 'ixia',
    SPIRENT = 'spirent',
}

export const devicesTypes = Object.values(DevicesType);

export type DevicesTypeSelect = DevicesType | '';

export type DevicesSearchRequestParams = {
    location?: DevicesLocationSelect;
    type?: DevicesTypeSelect;
    search?: DevicesSearch;
    showOnlyMy?: boolean;
    favoriteUserId?: User['pk'];
    schemeId?: SchemeID;
};

export type SelectedDeviceId = Device['pk'] | null;

export type SelectedBookingId = Booking['pk'] | null;

export type BookingsRequestParams = {
    device: Device['pk'];
    range: DateRange;
    requestMode: BookingsRequestMode;
};

export type BookingRequestParams = {
    id: Booking['pk'];
};

export type DevicesResponse = {
    dut: Device[];
    ixia: Device[];
    spirent: Device[];
};

export enum BookingDeviceModalMode {
    VIEW,
    CREATE,
    MANAGE,
}

export type BookingCalendarSelection = Record<string, boolean>;

export type SlotIndex = number;

export type ClosestBookingRequestParams = {
    device: Device['pk'];
    from: string;
    slots?: SlotIndex[];
};

export type CreateBookingRequestParams = {
    device: Booking['device'];
    booked_from: Booking['booked_from'];
    booked_to: Booking['booked_to'];
    comment: Booking['comment'];
    access: Booking['access'];
    // should_change_password?: boolean; // Disabled in https://jira.esafeline.com/browse/UTMS-3323
    // should_disable_pubkey?: boolean;
    slots?: Booking['slots'];
    selectMode?: boolean;
};

export type UpdateBookingRequestParams = {
    bookingId: Booking['pk'];
    booked_to: Booking['booked_to'];
    comment: Booking['comment'];
    access: Booking['access'];
    slots?: Booking['slots'];
};

export type CancelBookingRequestParams = {
    bookingId: Booking['pk'];
    isInProgress: boolean;
};

export type BookingsRequestMode = 'background-fetch' | 'standard';

export type BookingDeviceModalData = {
    mode: BookingDeviceModalMode;
    open: boolean;
    dateTime: Date;
    bookingForItem: Booking | null;
    slotIndex?: SlotIndex;
};

export enum BookingStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
    ERROR = 'ERROR',
}

export type DutBookingCredentials = {
    label: string;
    command: string;
    password: string;
};

export type TrafficGeneratorBookingCredentials = {
    link: string;
    login: string;
    password: string;
};

export type BookingFormErrorMessage = {
    message: string;
    field: 'datetime' | 'slots_count' | null;
};

export type SchemeHWDevice = Pick<Device, 'pk' | 'name' | 'office'> &
    Pick<DeviceFullInfo, 'image'> & {
        zone_connections: Record<string, string>;
    };

export type BookingCalendarConfig = Partial<{
    bookingRequiredPeriod: [Date, Date];
    minimumHourDuration: number;
    hideDevicesColumn: boolean;
    localBookingsStorageMode: boolean;
    selectMode: boolean;
}>;
